/**
 * AIConsentGate — compact, centered one-time consent for LexIQ's AI
 * features. Apple 5.1.1(i) & 5.1.2(i). No provider names in UI — the
 * Privacy Policy (linked inline, opened as an in-place overlay so we
 * never leave this modal) discloses Anthropic / OpenAI. Matches the
 * ChatGPT / Notion AI / Grammarly pattern. Once granted, permanent.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { Sparkle, X as XIcon, Check, ArrowSquareOut } from "@phosphor-icons/react";
import AIConsentPolicyDialog from "./AIConsentPolicyDialog";

const NAVY = "#0A192F";
const GOLD = "#D4AF37";
const GOLD_DARK = "#A88425";
const MUTED = "#5A6470";
const BODY = "#3F4750";

export default function AIConsentGate({ open, submitting, onAccept, onDeny }) {
  const [checked, setChecked] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  if (!open) return null;

  const canSubmit = checked && !submitting;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[130] bg-[#0A192F]/60 backdrop-blur-sm flex items-center justify-center p-4 font-tajawal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-consent-title"
        data-testid="ai-consent-gate"
        dir="rtl"
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-[380px] relative animate-ai-consent-in overflow-hidden"
          style={{
            background: "linear-gradient(180deg,#FFFFFF 0%,#FDFCF5 100%)",
            borderRadius: "24px",
            boxShadow: "0 24px 70px -20px rgba(10,25,47,0.5), 0 0 0 1px rgba(212,175,55,0.25) inset",
            border: "1px solid rgba(212,175,55,0.4)",
          }}
        >
          {/* Ambient gold glow at the top for a premium editorial feel */}
          <div
            className="absolute -top-24 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)`,
              filter: "blur(20px)",
            }}
          />

          <button
            type="button"
            onClick={onDeny}
            aria-label="إغلاق"
            data-testid="ai-consent-close"
            disabled={submitting}
            className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center transition disabled:opacity-40 z-10"
          >
            <XIcon size={13} weight="bold" color={NAVY} />
          </button>

          <div className="relative px-6 pt-9 pb-6 text-center">
            <div
              className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 relative"
              style={{
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
                boxShadow: `0 10px 24px -6px ${GOLD}aa, 0 0 0 6px ${GOLD}15`,
                transform: "rotate(-4deg)",
              }}
            >
              <Sparkle size={30} weight="fill" color="#FFFFFF" />
            </div>

            <h2
              id="ai-consent-title"
              className="text-[18px] font-extrabold leading-tight mb-1"
              style={{ color: NAVY, letterSpacing: "-0.01em" }}
            >
              المُساعِد القانونيّ الذَّكيّ
            </h2>
            <p
              className="text-[10.5px] font-bold uppercase tracking-wider mb-4"
              style={{ color: GOLD_DARK }}
            >
              إقرار لِمَرَّة واحِدة
            </p>

            <div
              className="mx-auto mb-5 h-px w-16"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
            />

            <p
              className="text-[12.5px] leading-[1.9] mb-5"
              style={{ color: BODY }}
            >
              نَستَعين بِنَماذج ذَكاء اصطِناعي مُتَقَدِّمة لِمُساعَدَتك في الأَسئلَة
              القانونيّة وَصياغَة المُستَنَدات. بِياناتك مُشَفَّرة وَلا تُستَخدَم لِتَدريب
              النَّماذِج.
            </p>

            <label
              className={`flex items-start gap-2.5 p-3 rounded-2xl transition-all border cursor-pointer text-right mb-3 ${
                checked
                  ? "bg-[#EFFBF1] border-[#16A34A]"
                  : "bg-white border-[#EAEAEA] hover:border-[#D4AF37] hover:bg-[#FFFDF5]"
              }`}
              data-testid="ai-consent-ack-row"
            >
              <span
                className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition ${
                  checked ? "bg-[#16A34A] text-white" : "bg-white border-2 border-[#5A6470]"
                }`}
              >
                {checked && <Check size={13} weight="bold" />}
              </span>
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={submitting}
                data-testid="ai-consent-ack-checkbox"
              />
              <span className="flex-1 min-w-0 text-[12px] leading-[1.7]" style={{ color: NAVY }}>
                بِتَأشيري، أُوافِق على مُعالَجَة إدخالاتي بِواسِطة الذَّكاء الاصطِناعي.
              </span>
            </label>

            <button
              type="button"
              onClick={() => setPolicyOpen(true)}
              data-testid="ai-consent-privacy-link"
              className="inline-flex items-center gap-1 text-[11.5px] font-bold mb-4 hover:opacity-80 transition"
              style={{ color: GOLD_DARK }}
            >
              <ArrowSquareOut size={12} weight="bold" />
              <span>قِراءَة سِياسة الخُصوصيّة الكامِلة</span>
            </button>

            <button
              type="button"
              onClick={onAccept}
              disabled={!canSubmit}
              data-testid="ai-consent-accept"
              className="w-full h-12 rounded-2xl font-extrabold text-[14px] text-white transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
              style={{
                background: canSubmit
                  ? `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
                  : "#E5E7EB",
                boxShadow: canSubmit
                  ? `0 8px 22px -4px ${GOLD}aa, 0 0 0 3px ${GOLD}22`
                  : "none",
                cursor: canSubmit ? "pointer" : "not-allowed",
                color: canSubmit ? "#FFFFFF" : "#9CA3AF",
              }}
            >
              {submitting ? "جاري الحِفظ…" : "مُتابَعَة"}
            </button>

            {!checked && (
              <p
                className="text-[10.5px] text-center mt-2.5 font-bold"
                style={{ color: MUTED }}
              >
                أَشِّر على المُرَبَّع لِتَفعيل الزِّرّ
              </p>
            )}
          </div>

          <style>{`
            @keyframes ai-consent-in {
              from { transform: translateY(12px) scale(0.96); opacity: 0; }
              to   { transform: translateY(0)   scale(1);    opacity: 1; }
            }
            .animate-ai-consent-in { animation: ai-consent-in 240ms cubic-bezier(0.22,1,0.36,1); }
          `}</style>
        </div>
      </div>

      <AIConsentPolicyDialog
        open={policyOpen}
        onClose={() => setPolicyOpen(false)}
      />
    </>,
    document.body,
  );
}
